import { useState, useEffect } from "react";
import { useNavigate, useLoaderData } from "react-router-dom";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { GET_COMMISSARY_ITEMS, GET_VENDORS_FOR_COMMISSARY } from "./queries";
import { CREATE_COMMISSARY_ORDER } from "./mutations";
import { organizationStore } from "../../store/organization";
import { eventStore } from "../../store/event";
import { userStore } from "../../store/user";
import { usdFormat } from "../../utils";
import toast from "react-hot-toast";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import CustomSelect from "../../components/customSelect";
import { CommissaryOrderItem } from "../../types/order-commissary";

const client = createApolloClient();
const { getState } = organizationStore;
const { getState: getEventStore } = eventStore;
const { getState: getUserStore } = userStore;

interface CommissaryItem {
  id: number;
  name: string;
  description?: string;
  category?: string;
  price: number;
  image_url?: string;
  is_active: boolean;
  event_id?: number;
  size?: string;
  order_quantity_limit?: number;
}

interface VendorOption {
  id: number;
  name: string;
  poc_name?: string;
  poc_phone?: string;
}

interface CartItem {
  item: CommissaryItem;
  quantity: number;
  notes: string;
}

export const commissaryOrderCreateLoader = async () => {
  const rawOrganizationId = getState().organizationId;
  const organizationId = parseInt(
    rawOrganizationId !== null ? rawOrganizationId.toString() : "",
    10
  );
  const eventId = getEventStore().eventId;

  const vendorWhere: any = {};
  if (organizationId && organizationId !== 0) {
    vendorWhere.organization_id = { _eq: organizationId };
  }
  vendorWhere.is_active = { _eq: true };

  const { data: vendorData } = await client.query({
    query: GET_VENDORS_FOR_COMMISSARY,
    variables: { where: vendorWhere },
  });

  return {
    vendors: vendorData.vendors as VendorOption[],
    eventId: eventId ? parseInt(eventId.toString(), 10) : null,
  };
};

export const CommissaryOrderCreate = () => {
  const navigate = useNavigate();
  const { vendors, eventId } = useLoaderData() as {
    vendors: VendorOption[];
    eventId: number | null;
  };

  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);
  const [smsNotify, setSmsNotify] = useState(false);
  const [notes, setNotes] = useState("");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [items, setItems] = useState<CommissaryItem[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!eventId) {
      setItems([]);
      return;
    }

    const fetchItems = async () => {
      setIsLoadingItems(true);
      try {
        const { data } = await client.query({
          query: GET_COMMISSARY_ITEMS,
          variables: {
            where: {
              event_id: { _eq: eventId },
              is_active: { _eq: true },
              is_commissary: { _eq: true },
            },
          },
        });
        setItems(data.items);
      } catch (error) {
        toast.error("Failed to load commissary items");
      } finally {
        setIsLoadingItems(false);
      }
    };

    fetchItems();
  }, [eventId]);

  const addToCart = (item: CommissaryItem) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.item.id === item.id);
      if (existing) {
        return prev.map((c) =>
          c.item.id === item.id ? { ...c, quantity: c.quantity + 1 } : c
        );
      }
      return [...prev, { item, quantity: 1, notes: "" }];
    });
  };

  const updateCartQuantity = (itemId: number, quantity: number) => {
    if (quantity <= 0) {
      setCart((prev) => prev.filter((c) => c.item.id !== itemId));
    } else {
      setCart((prev) =>
        prev.map((c) => (c.item.id === itemId ? { ...c, quantity } : c))
      );
    }
  };

  const updateCartNotes = (itemId: number, notes: string) => {
    setCart((prev) =>
      prev.map((c) => (c.item.id === itemId ? { ...c, notes } : c))
    );
  };

  const subtotal = cart.reduce(
    (sum, c) => sum + c.item.price * c.quantity,
    0
  );
  const total = subtotal;

  const handleSubmit = async () => {
    if (!selectedVendorId) {
      toast.error("Please select a vendor");
      return;
    }
    if (!eventId) {
      toast.error("Please select an event first");
      return;
    }
    if (cart.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    setIsSubmitting(true);
    try {
      const user = getUserStore().user;
      const orderItems: CommissaryOrderItem[] = cart.map((c) => ({
        item_id: c.item.id,
        item_name: c.item.name,
        quantity: c.quantity,
        unit_price: c.item.price,
        total_price: c.item.price * c.quantity,
        notes: c.notes || undefined,
      }));

      const { data } = await client.mutate({
        mutation: CREATE_COMMISSARY_ORDER,
        variables: {
          event_id: eventId,
          vendor_id: selectedVendorId,
          user_id: user?.id ? parseInt(user.id.toString(), 10) : null,
          notes: notes || null,
          subtotal,
          tax: 0,
          total,
          items: orderItems,
          sms_notify: smsNotify,
        },
      });

      toast.success("Commissary order created");
      navigate(
        `/commissary-orders/${data.insert_order_commissary_one.id}`
      );
    } catch (error) {
      toast.error("Failed to create order: " + error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const groupedItems = items.reduce<Record<string, CommissaryItem[]>>(
    (acc, item) => {
      const category = item.category || "Uncategorized";
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    },
    {}
  );

  return (
    <div className="max-w-5xl mx-auto py-6 px-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Create Commissary Order
        </h1>
        <SecondaryButton onClick={() => navigate("/commissary-orders/list")}>
          Cancel
        </SecondaryButton>
      </div>

      {/* Order Settings */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Order Details</h2>
        </div>
        <div className="px-6 py-4 grid grid-cols-6 gap-4">
          <div className="col-span-3">
            <CustomSelect
              title="Vendor"
              name="vendor_id"
              required
              onChange={(v) => setSelectedVendorId(parseInt(v, 10))}
              options={vendors.map((v) => ({
                label: v.name,
                value: v.id,
              }))}
            />
          </div>
          <div className="col-span-3">
            <label className="block text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Order notes..."
            />
          </div>
          <div className="col-span-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={smsNotify}
                onChange={(e) => setSmsNotify(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Send SMS notifications to vendor
              </span>
            </label>
          </div>
        </div>
      </div>

      {!eventId && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800">
            Please select an event from the event selector above to see
            available commissary items.
          </p>
        </div>
      )}

      {/* Item Selection */}
      {eventId && (
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Available Items
            </h2>
          </div>
          <div className="px-6 py-4">
            {isLoadingItems ? (
              <p className="text-sm text-gray-500">Loading items...</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-gray-500">
                No commissary items found for this event.
              </p>
            ) : (
              Object.entries(groupedItems).map(([category, categoryItems]) => (
                <div key={category} className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase">
                    {category}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {categoryItems.map((item) => {
                      const inCart = cart.find((c) => c.item.id === item.id);
                      return (
                        <div
                          key={item.id}
                          className={`border rounded-lg p-3 cursor-pointer hover:border-primary-400 transition-colors ${
                            inCart
                              ? "border-primary-500 bg-primary-50"
                              : "border-gray-200"
                          }`}
                          onClick={() => addToCart(item)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {item.name}
                              </p>
                              {item.size && (
                                <p className="text-xs text-gray-500">
                                  {item.size}
                                </p>
                              )}
                            </div>
                            <p className="text-sm font-medium text-gray-900">
                              {usdFormat.format(item.price / 100)}
                            </p>
                          </div>
                          {item.description && (
                            <p className="text-xs text-gray-500 mt-1">
                              {item.description}
                            </p>
                          )}
                          {inCart && (
                            <p className="text-xs text-primary-600 mt-1 font-medium">
                              In cart: {inCart.quantity}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Cart */}
      {cart.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Cart</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Item
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Qty
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Unit Price
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Notes
                </th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cart.map((c) => (
                <tr key={c.item.id}>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {c.item.name}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          updateCartQuantity(c.item.id, c.quantity - 1)
                        }
                        className="w-6 h-6 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-300"
                      >
                        -
                      </button>
                      <span className="text-sm w-8 text-center">
                        {c.quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          updateCartQuantity(c.item.id, c.quantity + 1)
                        }
                        className="w-6 h-6 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-300"
                      >
                        +
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">
                    {usdFormat.format(c.item.price / 100)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">
                    {usdFormat.format((c.item.price * c.quantity) / 100)}
                  </td>
                  <td className="px-6 py-4">
                    <input
                      type="text"
                      className="text-sm border-gray-300 rounded-md w-full"
                      placeholder="Item notes"
                      value={c.notes}
                      onChange={(e) =>
                        updateCartNotes(c.item.id, e.target.value)
                      }
                    />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      type="button"
                      onClick={() => updateCartQuantity(c.item.id, 0)}
                      className="text-red-600 text-sm hover:text-red-800"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td
                  colSpan={3}
                  className="px-6 py-3 text-sm font-bold text-gray-900 text-right"
                >
                  Total
                </td>
                <td className="px-6 py-3 text-sm font-bold text-gray-900 text-right">
                  {usdFormat.format(total / 100)}
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Submit */}
      <div className="flex justify-end gap-3">
        <SecondaryButton onClick={() => navigate("/commissary-orders/list")}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={handleSubmit} isDisabled={isSubmitting}>
          {isSubmitting ? "Creating..." : "Create Order"}
        </PrimaryButton>
      </div>
    </div>
  );
};
